/**
 * Coalesces duplicate concurrent calls that target the same key ("single-flight") — the single
 * reusable entry point the "business service" concurrency guards use.
 *
 * When a call for `key` is already running, `run` returns the SAME in-flight promise instead of
 * starting a second operation, so the duplicate caller resolves with the same result. Suppressing
 * a duplicate is an expected, successful outcome — NOT an error — so this never rejects on the
 * guard path; only the underlying operation's own failures reject.
 *
 * It replaces the previous per-service guard idiom `new Promise((resolve, reject) => reject())`,
 * which rejected with `undefined`; callers didn't handle that, so it surfaced as an unhandled
 * "Uncaught (in promise): [object Undefined]" (Sentry FRONTEND-3HT) even though the write had
 * succeeded. Keeping the map, the coalescing, and the in-flight accessors together here means the
 * guarding behaviour lives in one place instead of being re-implemented in every service.
 */
export class InFlightRegistry {

  private readonly inFlight = new Map<unknown, Promise<unknown>>();

  /**
   * Run `run` for `key`. If a call for `key` is already in flight, return that same promise
   * instead of starting a second one (coalesce). Never rejects on the guard path.
   */
  run<T>(key: unknown, run: () => Promise<T>): Promise<T> {
    const existing = this.inFlight.get(key) as Promise<T> | undefined;
    if (existing) {
      return existing;
    }

    const started = run().finally(() => this.inFlight.delete(key));
    this.inFlight.set(key, started);
    return started;
  }

  /** True while a call for `key` is in flight — e.g. for a button's loading/disabled state. */
  has(key: unknown): boolean {
    return this.inFlight.has(key);
  }

  /** The keys currently in flight, e.g. to back an `updating.includes(id)` accessor. */
  keys<K = unknown>(): K[] {
    return [...this.inFlight.keys()] as K[];
  }
}
